import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Avatar,
  InputAdornment,
  Chip,
  Divider,
  Card,
  CardContent,
  Snackbar,
  Alert
} from "@mui/material";
import { useLocation } from "react-router-dom";
import {
  Save as SaveIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  CardMembership as SubscriptionIcon,
  CloudUpload as UploadIcon
} from "@mui/icons-material";
import { investorApi } from "../../api/api";
 
 
export default function ChangeUserDetails() {
  const location = useLocation();
  const { UserData } = location.state || {};
 
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    subscription: "",
    joinedOn: "",
    avatar: "",
  });
 
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [errors, setErrors] = useState({});
 
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
 
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
 
  // Pre-fill form with UserData
  useEffect(() => {
    if (UserData) {
      const [first, ...lastParts] = (UserData.InvestorName || "").split(" ");
      setFormData({
        firstName: first || "",
        lastName: lastParts.join(" ") || "",
        email: UserData.Email || "",
        phoneNumber: UserData.PhoneNumber || "",
        subscription: UserData.Subscription || "",
        joinedOn: UserData.JoinedOn || "",
        avatar: UserData.Avatar || "",
      });
    }
  }, [UserData]);
 
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
 
  const validate = () => {
    let tempErrors = {};
    if (!formData.firstName) tempErrors.firstName = "First name is required";
    if (!formData.email) {
      tempErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      tempErrors.email = "Email is not valid";
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };
 
const handleSubmit = async (e) => {
  e.preventDefault();
  const userId=UserData.id
  if (!validate()) return;
 
  const formDatas = new FormData();
  formDatas.append("FirstName", formData.firstName);
  formDatas.append("LastName", formData.lastName);
  formDatas.append("Email", formData.email);
  formDatas.append("Mobilenumber", formData.phoneNumber);
 
  if (formData.avatar instanceof File) {
    formDatas.append("avatar", formData.avatar);
  }
 
  try {
    const response = await investorApi.updateProfile(userId, formDatas);
 
    if (response.status === 200) {
      showSnackbar("Profile Updated Successfully", "success");
    } else {
      showSnackbar("Something went wrong", "error");
    }
  } catch (error) {
    console.error(error);
    if (error.response && error.response.status === 409) {
      showSnackbar(error.response.data.message, "error");
    } else {
      showSnackbar("Failed to update profile", "error");
    }
  }
};
 
 
  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({ ...formData, avatar: event.target.result });
      };
      reader.readAsDataURL(file);
    }
  };
 
  return (
    <Grid
      container
      justifyContent="center"
      alignItems="flex-start"
      sx={{ minHeight: "100vh", backgroundColor: "grey.50", py: 4, px: 2 }}
    >
      <Grid item xs={12} md={10} lg={8}>
        <Paper
          elevation={0}
          sx={{
            p: 0,
            borderRadius: 4,
            overflow: "hidden",
            background: "white",
            boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #1976d2 0%, #004ba0 100%)",
              color: "white",
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Edit User Profile
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Update user information and preferences
            </Typography>
          </Box>
 
          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Left Column - Avatar and Read-only Info */}
              <Grid item xs={12} md={4}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Avatar
                    src={formData.avatar}
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 2,
                      border: "4px solid",
                      borderColor: "primary.light",
                    }}
                  >
                    {!formData.avatar && <PersonIcon sx={{ fontSize: 60 }} />}
                  </Avatar>
 
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="avatar-upload"
                    type="file"
                    onChange={handleAvatarUpload}
                  />
                  <label htmlFor="avatar-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      size="small"
                    >
                      Change Avatar
                    </Button>
                  </label>
 
                  <Card sx={{ mt: 4, width: "100%", borderRadius: 3 }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <SubscriptionIcon sx={{ mr: 1 }} /> Subscription
                      </Typography>
                      <Chip
                        label={
                          formData.subscription?.toLowerCase() === "yes"
                            ? "Active"
                            : "Inactive"
                        }
                        color={
                          formData.subscription?.toLowerCase() === "yes"
                            ? "success"
                            : "default"
                        }
                        variant="filled"
                        sx={{ width: "100%", py: 1 }}
                      />
 
                      <Divider sx={{ my: 2 }} />
 
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center", mt: 2 }}
                      >
                        <CalendarIcon sx={{ mr: 1 }} /> Joined On
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formData.joinedOn}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
 
              {/* Right Column - Editable Form */}
              <Grid item xs={12} md={8}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Grid>
 
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Grid>
 
                    <Grid item xs={12}>
                      <TextField
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Grid>
 
                    <Grid item xs={12}>
                      <TextField
                        label="Phone Number"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
 
                  <Box mt={4} textAlign="right">
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<SaveIcon />}
                      sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: "16px",
                        borderRadius: 3,
                        background:
                          "linear-gradient(135deg, #1976d2 0%, #004ba0 100%)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #004ba0 0%, #1976d2 100%)",
                        },
                      }}
                    >
                      Save Changes
                    </Button>
 
                    <Snackbar
                      open={snackbarOpen}
                      autoHideDuration={3000}
                      onClose={handleSnackbarClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                    >
                      <Alert
                        onClose={handleSnackbarClose}
                        severity={snackbarSeverity}
                        sx={{ width: "100%" }}
                      >
                        {snackbarMessage}
                      </Alert>
                    </Snackbar>
                  </Box>
                </form>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}